<script lang="ts" setup>
import { ref, computed, reactive, onMounted, Ref, ComponentPublicInstance } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useAuthStore, useUiStore } from '@/stores';
import { useVuelidateWithLogging } from '@/composables/useVuelidateWithLogging';
import { goBack } from '@/composables/useNavigation';
import { ContactService, SelectOptionsService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { required, maxLength } from '@/utils/validators';

import {
    Permission,
    FormAction,
    ContactModel,
    ContactFormState,
    SelectOptionsServiceGetUsersResult,
} from '@/types';

import { ContactEmailForm, ContactPhoneForm } from '@/views/pages/contact/components';

const props = defineProps({
    action: { type: String, default: FormAction.CREATE },
});

const route = useRoute();
const uiStore = useUiStore();
const authenticatedUserId = useAuthStore().user?.id;

const emailRefs = ref<Record<string, InstanceType<typeof ContactEmailForm> | null>>({});
const phoneRefs = ref<Record<string, InstanceType<typeof ContactPhoneForm> | null>>({});
const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const formState = reactive<ContactFormState>({
    name: null,
    emails: [],
    phones: [
        {
            number: null,
            _key: crypto.randomUUID(),
            isMain: true,
        },
    ],
    contactCompany: null,
    ownerUserId: authenticatedUserId ?? null,
});

const rules = computed(() => ({
    name: { required, maxLength: maxLength(255) },
    emails: {},
    phones: { required },
    contactCompany: {},
    ownerUserId: { required },
}));

const { v$, $validateWithLogging } = useVuelidateWithLogging(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                is_active: true,
            },
        });

        usersOptions.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchContact = async (id: number) => {
    try {
        await setLoading(true);
        const { data } = await ContactService.get<ContactModel>(id);
        formState.name = data.name;
        formState.emails = data.emails.map((email) => ({ ...email, _key: crypto.randomUUID() }));
        formState.phones = data.phones.map((phone) => ({ ...phone, _key: crypto.randomUUID() }));
        formState.contactCompany = data.contactCompany;
        formState.ownerUserId = data.ownerUserId;
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const setEmailRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        emailRefs.value[key] = el as InstanceType<typeof ContactEmailForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete emailRefs.value[key];
    }
};

const setPhoneRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        phoneRefs.value[key] = el as InstanceType<typeof ContactPhoneForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete phoneRefs.value[key];
    }
};

const addEmail = () => {
    formState.emails.push({
        email: null,
        _key: crypto.randomUUID(),
        isMain: formState.emails.length ? false : true,
    });
};

const addPhone = () => {
    formState.phones.push({ number: null, _key: crypto.randomUUID(), isMain: false });
};

const removeEmail = (index: number) => {
    const [removedEmail] = formState.emails.splice(index, 1);
    if (removedEmail.isMain && formState.emails.length > 0) formState.emails[0].isMain = true;
};

const removePhone = (index: number) => {
    if (formState.phones.length <= 1) return;

    const [removedPhone] = formState.phones.splice(index, 1);

    if (removedPhone.isMain) formState.phones[0].isMain = true;
};

const setMainPhone = (key: string) => {
    formState.phones.forEach((phone) => {
        phone.isMain = phone._key === key;
    });
};

const setMainEmail = (key: string) => {
    formState.emails.forEach((email) => {
        email.isMain = email._key === key;
    });
};

const onSubmit = async () => {
    const emailValidationResults = await Promise.all(
        Object.values(emailRefs.value).map((ref) => !ref || ref?.validate?.()),
    );

    const phoneValidationResults = await Promise.all(
        Object.values(phoneRefs.value).map((ref) => ref?.validate?.() ?? false),
    );

    const allValid = [...emailValidationResults, ...phoneValidationResults].every(Boolean);
    const isFormValid = await $validateWithLogging();

    if (!isFormValid || !allValid) return;

    await setLoading(true);

    const payload = {
        ...formState,
        emails: formState.emails.map(({ _key, ...email }) => email),
        phones: formState.phones.map(({ _key, ...phone }) => phone),
    };

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewContactById', params: { id } });
    };

    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await ContactService.update(id, payload);
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await ContactService.create(payload);
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        const contactId = Number(route.params.id);
        if (!isNaN(contactId)) fetchContact(contactId);
    }
});
</script>

<template>
    <form class="space-y-4 p-4 sm:p-6 lg:p-8" autocomplete="off" @submit.prevent="onSubmit">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('contact.' + props.action + '.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex items-center space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListContact' }" />
                    <UiButton
                        v-if="
                            (props.action == FormAction.CREATE &&
                                $can(Permission.CREATE_CONTACT)) ||
                            (props.action == FormAction.EDIT && $can(Permission.EDIT_CONTACT))
                        "
                        type="submit"
                        variant="primary"
                        text-variant="white"
                    >
                        {{ $t('core.actions.Save') }}
                    </UiButton>
                </div>
            </template>
        </UiPageHeader>
        <UiPanel>
            <div class="gap-4 md:grid md:grid-cols-2">
                <UiTextInput
                    v-model="v$.name.$model"
                    name="name"
                    type="text"
                    :label="$t('contact.labels.name')"
                    :errors="v$.name.$errors"
                    @blur="v$.name.$touch"
                />
            </div>
        </UiPanel>
        <UiPanel>
            <div class="flex">
                <h2 class="mb-2 flex-1 text-lg font-semibold text-gray-800">
                    {{ $t('contact.form.section.emails.title') }}
                </h2>
                <UiButton variant="primary" text-variant="white" @click="addEmail">
                    {{ $t('contact.form.actions.addEmail') }}
                </UiButton>
            </div>
            <div v-if="formState.emails.length === 0">
                <p>{{ $t('contact.errors.noEmails') }}</p>
            </div>
            <div class="gap-4 md:grid md:grid-cols-2">
                <ContactEmailForm
                    v-for="(item, index) in formState.emails"
                    :key="item._key"
                    :index="index"
                    v-model="formState.emails[index]"
                    :ref="(el) => setEmailRef(el, item._key)"
                    @remove="() => removeEmail(index)"
                    @set-main="() => setMainEmail(item._key)"
                />
            </div>
        </UiPanel>
        <UiPanel>
            <div class="flex">
                <h2 class="mb-2 flex-1 text-lg font-semibold text-gray-800">
                    {{ $t('contact.form.section.phones.title') }}
                </h2>
                <UiButton variant="primary" text-variant="white" @click="addPhone">
                    {{ $t('contact.form.actions.addPhone') }}
                </UiButton>
            </div>
            <div v-if="formState.phones.length === 0">
                <p>{{ $t('contact.errors.noPhones') }}</p>
            </div>
            <div
                v-if="v$.phones.$error"
                class="max-w-md gap-4 rounded-sm bg-red-100 px-4 py-3 text-sm text-red-700 shadow"
            >
                {{ v$.phones.$errors[0].$message }}
            </div>
            <div class="gap-4 md:grid md:grid-cols-2">
                <ContactPhoneForm
                    v-for="(item, index) in formState.phones"
                    :key="item._key"
                    :index="index"
                    :phones="formState.phones"
                    v-model="formState.phones[index]"
                    :ref="(el) => setPhoneRef(el, item._key)"
                    @remove="() => removePhone(index)"
                    @set-main="() => setMainPhone(item._key)"
                    :can-remove="formState.phones.length > 1"
                />
            </div>
        </UiPanel>
        <UiPanel>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <UiTextInput
                    name="contactCompany"
                    type="text"
                    v-model="v$.contactCompany.$model"
                    :label="$t('contact.form.labels.company')"
                />
                <div>
                    <label for="ownerUser" class="mb-2 block text-sm font-medium text-gray-800">
                        {{ $t('contact.form.labels.ownerUser') }}
                        <span class="text-xs">*</span>
                    </label>
                    <UiMultiselect
                        name="ownerUser"
                        id="ownerUser"
                        v-model="formState.ownerUserId"
                        :options="usersOptions"
                        :required="v$.ownerUserId.required !== undefined"
                        :errors="v$.ownerUserId?.$errors"
                        :disabled="!$can(Permission.EDIT_CONTACT_OWNER)"
                        primitive
                        label="name"
                        value-prop="id"
                    />
                </div>
            </div>
        </UiPanel>
        <UiCheckDirtyBeforeRouteLeave :dirty="v$.$anyDirty" />
    </form>
</template>
